<template>
  <TransitionRoot appear :show="true" as="template">
    <Dialog as="div" class="relative z-10" @close="closeModal">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="
                w-full
                max-w-2xl
                transform
                overflow-hidden
                rounded-xl
                bg-white
                text-left
                align-middle
                shadow-xl
                transition-all
                p-4
              "
            >
              <DialogTitle
                as="div"
                class="
                  p-5
                  md
                  mb-1
                  font-bold
                  container
                  inline-flex
                  justify-between
                  max-w-full
                  leading-6
                  col-md-6
                "
              >
                <h2 class="block col-md-6 text-left text-xl text-vice-teal-900">
                  Test session and connection requirements
                </h2>
                <svg
                  class="cursor-pointer"
                  @click="closeModal"
                  xmlns="http://www.w3.org/2000/svg"
                  width="29"
                  height="27"
                  viewBox="0 0 29 27"
                  fill="none"
                >
                  <path
                    d="M19.3407 20.7189L19.6858 21.0483L20.031 20.7189L21.5879 19.2332L21.967 18.8715L21.5879 18.5097L16.7125 13.8572L21.5879 9.20462L21.967 8.84289L21.5879 8.48117L20.031 6.99545L19.6858 6.66604L19.3407 6.99545L14.4313 11.6803L9.52205 6.99545L9.17686 6.66604L8.83167 6.99545L7.27478 8.48117L6.89573 8.84289L7.27478 9.20462L12.1502 13.8572L7.27478 18.5097L6.89573 18.8715L7.27478 19.2332L8.83167 20.7189L9.17686 21.0483L9.52205 20.7189L14.4313 16.034L19.3407 20.7189ZM1.30859 13.8572C1.30859 6.91225 7.09536 1.35718 14.4313 1.35718C21.7673 1.35718 27.5541 6.91225 27.5541 13.8572C27.5541 20.8021 21.7673 26.3572 14.4313 26.3572C7.09536 26.3572 1.30859 20.8021 1.30859 13.8572Z"
                    fill="#14B8A6"
                    stroke="black"
                  />
                </svg>
              </DialogTitle>
              <DialogOverlay class="px-5 py-1">
                <h4 class="text-vice-teal-900 text-lg mb-10">
                  Your network should be configured according to our guide. Please verify that all
                  domains and ports
                  <br />
                  are whitelisted and test your video conference connection below.
                </h4>

                <div class="col-span-2 block mb-4">
                  <div class="relative">
                    <div class="absolute inset-0 flex items-center" aria-hidden="true">
                      <div class="w-full border-t border-gray-300" />
                    </div>

                    <div class="relative flex justify-between">
                      <span class="pr-3 pt-1 bg-white text-lg font-medium text-vice-teal-900">
                        Connection requirements
                      </span>
                      <span class="pl-3 bg-white">
                        <Button
                          class="justify-end"
                          :disabled="checkRunning"
                          @click="detailedCheckAllResources"
                        >
                          Refresh
                        </Button>
                      </span>
                    </div>
                  </div>
                </div>
                <div class="container max-w-full inline-flex justify-between text-sm">
                  <table class="table-fixed min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th
                          v-for="(label, index, key) in table.tableColumns"
                          :key="index"
                          scope="col"
                          :class="
                            key === Object.keys(table.tableColumns).length - 1
                              ? 'text-right'
                              : 'text-left'
                          "
                          class="p-3 text-xs font-semibold text-gray-900 w-1/4 tracking-wider"
                        >
                          {{ label }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(row, rowIndex) in table.tableRows"
                        :key="rowIndex"
                        class="w-full border-t border-gray-300"
                      >
                        <td
                          class="p-3 text-xs font-medium text-gray-700 leading-8 w-1/4"
                          v-for="(column, colIndex, key) in table.tableColumns"
                          :key="colIndex"
                          :class="
                            key === Object.keys(table.tableColumns).length - 1
                              ? 'text-right'
                              : 'text-left'
                          "
                        >
                          <RefreshIcon
                            v-if="colIndex === 'status' && checkRunning"
                            class="mr-3 spin"
                          />
                          <span v-else :class="colIndex === 'status' ? row.class : ''">
                            {{ row[colIndex] }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-if="hasFailed" class="flex justify-end h-8">
                  <button
                    v-if="hasFailed"
                    @click="toggleIntercom"
                    type="button"
                    class="
                      inline-flex
                      items-center
                      px-4
                      border
                      text-xxs
                      font-medium
                      rounded-md
                      text-gray-500
                      bg-white
                    "
                  >
                    Get help
                  </button>
                </div>
              </DialogOverlay>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
<script>
import {
  Dialog,
  DialogOverlay,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';

import { showArticle } from '@intercom/messenger-js-sdk';
import {
  CHAT_DOMAIN,
  DEVICE_SCREEN_SHARE_APP_DOMAIN,
  JITSI_SERVER_DOMAIN,
  LARAVEL_ECHO_SERVER_HOST,
  LARAVEL_ECHO_SERVER_PORT,
  LARAVEL_ECHO_SERVER_SCHEME,
  NODE_WEBSOCKET_DOMAIN,
} from '@/Utils/vice-constants';

import Button from '@/Jetstream/Button.vue';
import RefreshIcon from '@/Components/Icons/RefreshIcon.vue';

const DEFAULT_PORT = 443;
const FETCH_TIMEOUT = 15000;
const INTERCOM_ARTICLE = '9776046';
const DEFAULT_SCHEME = 'https';
const HOSTS = [
  {
    scheme: LARAVEL_ECHO_SERVER_SCHEME,
    host: LARAVEL_ECHO_SERVER_HOST,
    port: process.env.NODE_ENV === 'production' ? DEFAULT_PORT : LARAVEL_ECHO_SERVER_PORT,
    path: process.env.NODE_ENV === 'production' ? '/ws' : '',
    purpose: 'VCE Application',
    status: '',
    class: null,
    passed: false,
  },
  {
    scheme: DEFAULT_SCHEME,
    host: JITSI_SERVER_DOMAIN,
    port: DEFAULT_PORT,
    path: '',
    purpose: 'VCE Server',
    status: '',
    class: null,
    passed: false,
  },
  {
    scheme: DEFAULT_SCHEME,
    host: CHAT_DOMAIN,
    port: DEFAULT_PORT,
    path: '',
    purpose: 'VCE Chat',
    status: '',
    class: null,
    passed: false,
  },
  {
    scheme: 'wss',
    host: NODE_WEBSOCKET_DOMAIN,
    port: DEFAULT_PORT,
    path: '',
    purpose: 'Smart glass server',
    status: '',
    class: null,
    passed: false,
  },
  {
    scheme: DEFAULT_SCHEME,
    host: DEVICE_SCREEN_SHARE_APP_DOMAIN,
    port: DEFAULT_PORT,
    path: '',
    purpose: 'Smart phone server',
    status: '',
    class: null,
    passed: false,
  },
  // {
  //   scheme: DEFAULT_SCHEME,
  //   host: RECORDING_SERVER_DOMAIN,
  //   port: DEFAULT_PORT,
  //   path: '',
  //   purpose: 'Recording Server',
  //   status: '',
  //   class: null,
  //   passed: false,
  // },
];

export default {
  name: 'TestHostsAndPorts',
  emits: ['serverConnectionCheck', 'toggleTestHostsAndPortsModal'],
  components: {
    RefreshIcon,
    Button,
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogPanel,
    DialogTitle,
    DialogOverlay,
  },
  data() {
    return {
      classSucceedStyle: 'text-green-600',
      classFailedStyle: 'text-red-600',
      checkRunning: false,
      hasFailed: false,
      report: {},
      table: {
        tableColumns: {
          purpose: 'VCE Service',
          status: 'Status',
        },
        tableRows: HOSTS,
      },
    };
  },
  mounted() {
    this.detailedCheckAllResources();
  },

  methods: {
    async detailedCheckAllResources() {
      this.checkRunning = true;
      const results = await Promise.all(
        this.table.tableRows.map((host) => this.checkResource(host)),
      );

      this.report = results;

      results.forEach((result) => {
        const row = this.table.tableRows.find(
          (r) => r.host === result.host && r.port === result.port,
        );

        row.passed = result.passed;
        row.status = result.passed ? 'Connection established' : 'Connection failed';
        row.class = result.passed ? 'text-green-600' : 'text-red-600';
      });

      const passedValues = this.table.tableRows.map((check) => check.passed);
      const hasFailed = passedValues.some((value) => value === false);

      setTimeout(() => {
        this.$emit('serverConnectionCheck', !hasFailed);
        this.checkRunning = false;
        this.hasFailed = hasFailed;
      }, 1000);

      return !hasFailed;
    },

    async shortCheckAllResources() {
      return Promise.all(HOSTS.map((host) => this.checkResource(host)));
    },

    async checkResource(resource) {
      if (['wss', 'ws'].includes(resource.scheme)) {
        return this.checkWebSocketAvailability(
          resource.scheme,
          resource.host,
          resource.port,
          resource.path,
        );
      }

      return this.checkHostPortAvailability(
        resource.scheme,
        resource.host,
        resource.port,
        resource.path,
      );
    },

    async checkHostPortAvailability(scheme, host, port, path) {
      const controller = new AbortController();
      const signal = controller.signal;
      const timeout = setTimeout(() => {
        controller.abort();
      }, FETCH_TIMEOUT);

      try {
        const response = await fetch(`${scheme}://${host}:${port}${path}`, {
          method: 'HEAD',
          signal,
        });
        clearTimeout(timeout);
        if (response.ok) {
          return { scheme, host, port, passed: true };
        }
        return { scheme, host, port, passed: false };
      } catch (error) {
        return {
          host,
          port,
          passed: false,
          error: error.name === 'AbortError' ? 'Request Timeout' : error.message,
        };
      }
    },

    async checkWebSocketAvailability(scheme, host, port, path) {
      return new Promise((resolve) => {
        let timeOut = false;
        const result = { scheme, host, port };
        const ws = new WebSocket(
          `${scheme}://${host}:${port}${path}/socket.io/?EIO=4&transport=websocket`,
        );
        const timer = setTimeout(() => {
          timeOut = true;
          ws.close();
          resolve({ ...result, passed: false, error: 'Request Timeout' });
        }, FETCH_TIMEOUT);

        ws.onopen = () => {
          if (!timeOut) {
            clearTimeout(timer);
            resolve({ ...result, passed: true });
          }
        };

        ws.onerror = () => {
          if (!timeOut) {
            clearTimeout(timer);
            resolve({ ...result, passed: false, error: 'Websocket Error' });
          }
        };
      });
    },

    toggleIntercom() {
      showArticle(INTERCOM_ARTICLE);
    },

    closeModal() {
      this.$emit('toggleTestHostsAndPortsModal', false);
    },
  },
};
</script>

<style>
.spin {
  animation: spin-animation 0.5s infinite reverse;
  display: inline-block;
}

@keyframes spin-animation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(359deg);
  }
}
</style>
